import React, { FC, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ShoppingCart,
} from "@mui/icons-material";

import { FileText, Trash2, CheckCircle2, CheckCheck } from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";
import { Badge as ShadBadge } from "@/components/ui/badge";
import { GetRechnungDetailsDto } from "../../../../../api-client";
import { useDownload } from "../../../../../hooks/useDownload";
import { formatDateUI } from "../../../../../helpers/dateFormat";
import { canCreateReminder } from "../utility/CanCreateReminder";
import { WaitingBackdrop } from "../../../Base/components/BackDrop";

interface RechnungTableProps {
  transactions: GetRechnungDetailsDto[];
  onPay: (rechnung: GetRechnungDetailsDto) => void;
  onDelete: (rechnung: GetRechnungDetailsDto) => void;
  onCreateReminder: (rechnung: GetRechnungDetailsDto) => Promise<void> | void;
}

/**
 * Rechnung (Invoice) specific table component
 * Handles complex invoice structure with collapsible allocations
 * Implements SRP by focusing only on invoice rendering
 */
export const RechnungTable: FC<RechnungTableProps> = ({
  transactions,
  onPay,
  onDelete,
  onCreateReminder,
}) => {
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [loadingReminders, setLoadingReminders] = useState<Record<string, boolean>>({});
  const { handleDownloadPdf } = useDownload();

  const handleCreateReminderWithLoading = async (rechnung: GetRechnungDetailsDto) => {
    setLoadingReminders((prev) => ({ ...prev, [rechnung.id]: true }));
    try {
      await onCreateReminder(rechnung);
    } finally {
      setLoadingReminders((prev) => ({ ...prev, [rechnung.id]: false }));
    }
  };

  const getMahnungColor = (level: number) => {
    switch (level) {
      case 1:
        return "#f59e0b"; // amber
      case 2:
        return "#ea580c"; // orange
      case 3:
        return "#dc2626"; // red
      default:
        return "#dc2626";
    }
  };

  const handleToggleRow = (rechnungNumber: string) => {
    setOpenRows((prev) => ({
      ...prev,
      [rechnungNumber]: !prev[rechnungNumber],
    }));
  };

  const headers = [
    "",
    "Rechnung-Nr.",
    "Rezept-Nr.",
    "Rezeptdatum",
    "Patient",
    "Erstellt von",
    "Mandant",
    "Erstellt am",
    "Originalpreis (€)",
    "Gesaamtpreis (€)",
    "Bezahlt (€)",
    "Fällig (€)",
    "Status",
    "Dokumentieren",
    "Mahnung Level",
    "Aktionen",
  ];

  const sortedTransactions = [...transactions].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const isReminderLoading = Object.values(loadingReminders).some(Boolean);

  return (
    <>
      <TableContainer component={Paper} className="scrollable" sx={{ maxHeight: "650px" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => {
                const isPrice = [
                  "Originalpreis (€)",
                  "Gesaamtpreis (€)",
                  "Bezahlt (€)",
                  "Fällig (€)",
                ].includes(header);
                const isActions = header === "Aktionen";
                const isCenter = header === "Status";
                return (
                  <TableCell
                    key={idx}
                    align={isActions || isCenter ? "center" : isPrice ? "right" : "left"}
                    sx={{ fontWeight: "bold" }}
                    className="table-header-cell-rechnung"
                  >
                    {header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTransactions && sortedTransactions.length > 0 ? (
              sortedTransactions.map((rechnung) => {
                const isOpen = openRows[rechnung.rechnungNumber] || false;
                return (
                  <React.Fragment key={rechnung.rechnungNumber}>
                    {/* Main Row */}
                    <TableRow>
                      {/* Expand/Collapse Icon */}
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRow(rechnung.rechnungNumber)}
                          aria-label="expand row"
                        >
                          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.rechnungNumber}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.rxNumber ?? <ShoppingCart />}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.rxDate ? formatDateUI(rechnung.rxDate) : "Keine Datum"}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {`${rechnung.patientLastName}, ${rechnung.patientFirstName}`}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.userName || ""}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.accountName || ""}
                      </TableCell>
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {formatDateUI(rechnung.createdAt)}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {Number(rechnung.amount).toFixed(2).replace(".", ",") + " €"}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {Number(rechnung.amountWithMahnung).toFixed(2).replace(".", ",") + " €"}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {Number(rechnung.totalPaid).toFixed(2).replace(".", ",") + " €"}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {Number(rechnung.due).toFixed(2).replace(".", ",") + " €"}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {rechnung.paymentStatus}
                      </TableCell>

                      {/* Documents */}
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {rechnung.docDto?.id && (
                            <Box sx={{ position: "relative", display: "inline-flex" }}>
                              <Tooltip title="Rechnung PDF">
                                <ShadButton
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6"
                                  onClick={() => handleDownloadPdf(rechnung.docDto!.id)}
                                >
                                  <FileText />
                                </ShadButton>
                              </Tooltip>
                              {rechnung.emailSentAt && (
                                <Tooltip
                                  title={`Rechnung per E-Mail gesendet am ${formatDateUI(rechnung.emailSentAt)}`}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      position: "absolute",
                                      top: -2,
                                      left: -2,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#2e7d32",
                                    }}
                                  >
                                    <CheckCheck size={16} />
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                          {rechnung.billReminders
                            ?.slice()
                            .sort((a, b) => a.level - b.level)
                            .map((reminder) =>
                              reminder.document?.id ? (
                                <Box
                                  component="span"
                                  key={reminder.id}
                                  sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Tooltip
                                    title={`${reminder.level}. Mahnung – ${formatDateUI(reminder.createdAt)}`}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDownloadPdf(reminder.document!.id)}
                                      sx={{ p: "5px" }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: "6px",
                                          bgcolor: getMahnungColor(reminder.level),
                                          color: "#fff",
                                          fontSize: "14px",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {reminder.level}
                                      </Box>
                                    </IconButton>
                                  </Tooltip>
                                  {(reminder.emailSentAt || reminder.sentByEmail) && (
                                    <Tooltip
                                      title={
                                        reminder.emailSentAt
                                          ? `Per E-Mail gesendet am ${formatDateUI(reminder.emailSentAt)}`
                                          : "Per E-Mail gesendet"
                                      }
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          position: "absolute",
                                          top: -2,
                                          left: -2,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#2e7d32",
                                        }}
                                      >
                                        <CheckCheck size={15} />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              ) : null,
                            )}
                        </Box>
                      </TableCell>

                      {/* Mahnung Level */}
                      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 14 }}>
                        {(() => {
                          if (!rechnung.billReminders?.length) return "N/A";
                          const sorted = [...rechnung.billReminders].sort(
                            (a, b) =>
                              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                          );
                          return sorted[0].level;
                        })()}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" sx={{ padding: "5px", fontSize: 14 }}>
                        {Number(rechnung.due.toFixed(2)) > 0 ? (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                            <ShadButton
                              size="sm"
                              className="h-7 rounded-lg border-0 bg-gradient-to-b from-[#5aa6c4] to-[#4189a8] px-1.5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(65,137,168,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-200 hover:from-[#4f9cbb] hover:to-[#3a7f9c] hover:shadow-[0_2px_8px_rgba(65,137,168,0.45),inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:ring-2 focus-visible:ring-[#5aa6c4]/40 focus-visible:ring-offset-1 active:scale-[0.97]"
                              onClick={() => onPay(rechnung)}
                            >
                              Ausbuchen
                            </ShadButton>
                            <ShadButton
                              size="sm"
                              disabled={
                                !canCreateReminder(rechnung) || !!loadingReminders[rechnung.id]
                              }
                              className="h-7 rounded-lg border-0 bg-gradient-to-b from-[#f4b638] to-[#e08e0b] px-1.5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(224,142,11,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-200 hover:from-[#efac24] hover:to-[#c67d09] hover:shadow-[0_2px_8px_rgba(224,142,11,0.45),inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:ring-2 focus-visible:ring-[#f4b638]/40 focus-visible:ring-offset-1 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50"
                              onClick={() => handleCreateReminderWithLoading(rechnung)}
                            >
                              Mahnung
                            </ShadButton>
                            <Tooltip title="Rechnung löschen">
                              <ShadButton
                                size="icon"
                                className="h-7 w-7 border-0 bg-transparent text-slate-500 shadow-none transition-all duration-200 hover:scale-110 hover:bg-transparent hover:text-slate-700 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 [&_svg]:size-5"
                                onClick={() => onDelete(rechnung)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </ShadButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <ShadBadge
                            variant="outline"
                            className="gap-1 border-emerald-200 bg-emerald-200 px-2.5 py-1 text-sm font-medium text-emerald-800"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Vollständig bezahlt
                          </ShadBadge>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Row - Allocations */}
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={headers.length}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            <Typography variant="subtitle1" gutterBottom component="div">
                              Banküberweisungen
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Transaktionnummer</TableCell>
                                  <TableCell>Bezahlter Betrag (€)</TableCell>
                                  <TableCell>Bezahlt am</TableCell>
                                  <TableCell>Erstellt von</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rechnung.allocations && rechnung.allocations.length > 0 ? (
                                  rechnung.allocations.map((alloc) => (
                                    <TableRow key={alloc.transactionNumber}>
                                      <TableCell>{alloc.transactionNumber}</TableCell>
                                      <TableCell>
                                        {Number(alloc.paidAmount).toFixed(2).replace(".", ",")} €
                                      </TableCell>
                                      <TableCell>{formatDateUI(alloc.createdAt)}</TableCell>
                                      <TableCell>
                                        {`${alloc.userLastName}, ${alloc.userFirstName}`}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={4}>
                                      Keine Banküberweisungen vorhanden
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length}>
                  <Typography align="center">
                    Für die ausgewählten Daten wurden keine Transaktionen gefunden.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <WaitingBackdrop isWorking={isReminderLoading} message="Mahnung wird erstellt..." />
    </>
  );
};
